/*!
 * Almagrove Jewellers — Shopify theme
 * Handcrafted by Sector 7 · https://sector7hq.co
 * © 2026 Sector7HQ Ltd. All rights reserved.
 *
 * alm-cart-drawer.js — minimal, dependency-free cart drawer.
 * Responsibilities:
 *   1. Open on header cart-icon click (and any [data-alm-cart-open] trigger)
 *   2. Open on any add-to-cart form submit (intercepted, AJAX add via /cart/add.js)
 *   3. Quantity +/- and Remove on items (AJAX /cart/change.js)
 *   4. Free-shipping progress bar
 *   5. Re-render contents from /cart.js after every mutation
 *   6. Esc and overlay close, focus trap, body scroll lock
 *
 * Loaded via snippets/almagrove-cart-drawer.liquid (rendered once in theme.liquid).
 */
(function () {
  'use strict';

  if (window.__almCartDrawerBound) return;
  window.__almCartDrawerBound = true;

  var drawer, panel, body, footer, count, subtotal, progressFill, progressText, progress, threshold;
  var noteWrap, noteInput, noteStatus, noteSaveTimer = null;
  var xsellWrap, headerCountBadge;
  var lastFocused = null;
  var lastItemCount = 0;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  // ---------- open / close ----------
  function open() {
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    // Force reflow before adding the class so the transition runs
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    document.body.classList.add('alm-cart-drawer-open');
    // Pull fresh data so any out-of-band cart changes (other tabs) are reflected
    refresh();
    // Focus the panel for keyboard users after animation begins
    setTimeout(function () { if (panel) panel.focus(); }, 60);
  }

  function close() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('alm-cart-drawer-open');
    setTimeout(function () { drawer.setAttribute('hidden', ''); }, 320);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  // ---------- render ----------
  function renderItem(line) {
    // Match the Liquid snippet's HTML so styles apply identically
    var li = document.createElement('li');
    li.className = 'alm-cart-drawer__item';
    li.dataset.cartItemKey = line.key;

    var img = '';
    if (line.image) {
      var src = line.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_200x$1');
      img = '<img src="' + src + '" alt="" loading="lazy" width="80" height="96">';
    }
    var variantLine = '';
    if (!line.product_has_only_default_variant) {
      var opts = line.options_with_values;
      if (opts && opts.length) {
        opts.forEach(function (opt) {
          variantLine += '<p class="alm-cart-drawer__item-variant">' + escape(opt.name) + ': ' + escape(opt.value) + '</p>';
        });
      } else if (line.variant_title) {
        variantLine = '<p class="alm-cart-drawer__item-variant">' + escape(line.variant_title) + '</p>';
      }
    }

    var propsLine = '';
    if (line.properties && typeof line.properties === 'object') {
      Object.keys(line.properties).forEach(function (key) {
        var val = line.properties[key];
        if (val !== '' && val !== null && val !== undefined && key.charAt(0) !== '_') {
          var valStr = String(val);
          var valHtml = (valStr.indexOf('/uploads/') !== -1)
            ? '<a href="' + escape(valStr) + '" target="_blank" rel="noopener">' + escape(valStr.split('/').pop()) + '</a>'
            : escape(valStr);
          propsLine += '<p class="alm-cart-drawer__item-variant">' + escape(key) + ': ' + valHtml + '</p>';
        }
      });
    }

    var priceHtml;
    if (line.original_line_price > line.final_line_price) {
      priceHtml =
        '<span class="alm-cart-drawer__item-price-compare">' + formatMoney(line.original_line_price) + '</span>' +
        '<strong class="alm-cart-drawer__item-price-sale">' + formatMoney(line.final_line_price) + '</strong>';
    } else {
      priceHtml = '<strong>' + formatMoney(line.final_line_price) + '</strong>';
    }

    li.innerHTML =
      '<a href="' + line.url + '" class="alm-cart-drawer__item-image">' + img + '</a>' +
      '<div class="alm-cart-drawer__item-body">' +
        '<a href="' + line.url + '" class="alm-cart-drawer__item-title">' + escape(line.product_title || line.title || '') + '</a>' +
        variantLine +
        propsLine +
        '<div class="alm-cart-drawer__item-controls">' +
          '<div class="alm-cart-drawer__qty" data-alm-qty>' +
            '<button type="button" class="alm-cart-drawer__qty-btn" data-alm-qty-step="-1" aria-label="Decrease">−</button>' +
            '<input type="number" class="alm-cart-drawer__qty-input" value="' + line.quantity + '" min="0" data-alm-qty-input aria-label="Quantity">' +
            '<button type="button" class="alm-cart-drawer__qty-btn" data-alm-qty-step="1" aria-label="Increase">+</button>' +
          '</div>' +
          '<button type="button" class="alm-cart-drawer__remove" data-alm-cart-remove>Remove</button>' +
        '</div>' +
      '</div>' +
      '<div class="alm-cart-drawer__item-price">' + priceHtml + '</div>';

    return li;
  }

  function escape(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function renderEmpty() {
    return (
      '<div class="alm-cart-drawer__empty">' +
        '<svg viewBox="0 0 64 64" width="56" height="56" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4">' +
          '<path d="M14 18h36l-3 28a4 4 0 0 1-4 3.6H21a4 4 0 0 1-4-3.6Z"/>' +
          '<path d="M22 18a10 10 0 1 1 20 0"/>' +
        '</svg>' +
        '<p class="alm-cart-drawer__empty-title">Your bag is empty</p>' +
        '<p class="alm-cart-drawer__empty-body">Discover engagement rings, bespoke commissions, and timepieces.</p>' +
        '<a href="/collections/all-vintage-pre-loved-jewellery" class="alm-cart-drawer__empty-cta">Browse jewellery</a>' +
      '</div>'
    );
  }

  function bump(el) {
    if (!el) return;
    el.classList.remove('is-bumping');
    void el.offsetWidth; // restart animation
    el.classList.add('is-bumping');
  }

  function paint(cart) {
    // Hard guard — if /cart.js or /cart/change.js returns a malformed response
    // (rate-limited, redirected to login, blocked by an app, etc.) bail out
    // BEFORE we mutate the DOM. Otherwise undefined was rendering as the literal
    // string "undefined" (uppercased to "UNDEFINED" by .alm-cart-drawer__title).
    if (!cart || typeof cart !== 'object' || typeof cart.item_count !== 'number') {
      return;
    }
    var prevCount = lastItemCount;
    lastItemCount = cart.item_count;

    // Header / drawer count
    if (count) {
      count.textContent = String(cart.item_count);
      if (cart.item_count === 0) count.setAttribute('hidden', '');
      else count.removeAttribute('hidden');
    }

    // Body
    if (cart.item_count === 0) {
      body.innerHTML = renderEmpty();
      if (footer) footer.setAttribute('hidden', '');
      if (progress) progress.setAttribute('hidden', '');
      if (noteWrap) noteWrap.setAttribute('hidden', '');
      if (xsellWrap) xsellWrap.setAttribute('hidden', '');
    } else {
      var ul = document.createElement('ul');
      ul.className = 'alm-cart-drawer__items';
      ul.dataset.almCartItems = '';
      ul.setAttribute('role', 'list');
      cart.items.forEach(function (line) { ul.appendChild(renderItem(line)); });
      body.innerHTML = '';
      body.appendChild(ul);
      if (footer) footer.removeAttribute('hidden');
      if (progress) progress.removeAttribute('hidden');
      if (noteWrap) noteWrap.removeAttribute('hidden');
      if (xsellWrap) xsellWrap.removeAttribute('hidden');
    }

    // Subtotal
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    // Sync note input value (Shopify might have changed `cart.note` from elsewhere)
    if (noteInput && document.activeElement !== noteInput) {
      noteInput.value = cart.note || '';
    }

    // Free-shipping progress
    if (progressFill && threshold > 0) {
      var pct = Math.min(100, (cart.total_price / threshold) * 100);
      progressFill.style.width = pct + '%';
      var met = cart.total_price >= threshold;
      progress.classList.toggle('is-met', met);
      if (progressText) {
        if (met) {
          progressText.innerHTML = 'You qualify for <strong>FREE UK delivery</strong>.';
        } else {
          var remaining = threshold - cart.total_price;
          progressText.innerHTML = 'Spend <strong>' + formatMoney(remaining) + '</strong> more for FREE UK delivery.';
        }
      }
    }

    // Update header cart count too
    $$('.alm-header__cart-count, [data-cart-count]').forEach(function (b) {
      b.textContent = String(cart.item_count);
      if (cart.item_count > 0) b.removeAttribute('hidden');
      else b.setAttribute('hidden', '');
    });

    // Bump count badges if count went UP (added something)
    if (cart.item_count > prevCount) {
      bump(count);
      bump(headerCountBadge);
    }

    // Sync cross-sells — hide any whose product is already in the cart, show
    // it back if the customer removes it. Avoids "buy again the thing already
    // in your bag" awkwardness on AJAX add/remove without re-rendering Liquid.
    var inCart = {};
    cart.items.forEach(function (line) { inCart[String(line.product_id)] = true; });
    $$('.alm-cart-drawer__xsell-item', drawer).forEach(function (item) {
      var pid = item.getAttribute('data-product-id');
      if (inCart[pid]) item.setAttribute('hidden', '');
      else item.removeAttribute('hidden');
    });
  }

  function refresh() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(paint)
      .catch(function () { /* silent */ });
  }

  // ---------- mutate ----------
  function changeLine(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); }).then(paint);
  }

  function addItem(formData) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || 'Add failed'); });
      return r.json();
    }).then(function () { return refresh(); });
  }

  // ---------- delegated handlers ----------
  function bindDelegation() {
    document.addEventListener('click', function (ev) {
      // Close on click outside the panel
      if (drawer && drawer.classList.contains('is-open') && panel && !panel.contains(ev.target)) {
        close();
        return;
      }
      // Close button (X) or overlay via data attribute
      if (ev.target.closest('[data-alm-cart-close]')) {
        ev.preventDefault();
        close();
        return;
      }
      // Open trigger (header cart icon, or any explicit trigger).
      // BUT exclude clicks inside the drawer itself — otherwise "View cart"
      // (href="/cart") inside the drawer just re-opens the drawer instead of
      // navigating the user to the full cart page.
      var trigger = ev.target.closest('[data-alm-cart-open], a[href$="/cart"], a[href*="/cart?"]');
      if (trigger && drawer && !drawer.contains(trigger) && !ev.metaKey && !ev.ctrlKey && !ev.shiftKey) {
        ev.preventDefault();
        open();
        return;
      }
      // Quantity buttons
      var qBtn = ev.target.closest('[data-alm-qty-step]');
      if (qBtn) {
        ev.preventDefault();
        var item = qBtn.closest('[data-cart-item-key]');
        var input = qBtn.parentElement.querySelector('[data-alm-qty-input]');
        var step = Number(qBtn.dataset.almQtyStep);
        var next = Math.max(0, Number(input.value) + step);
        input.value = String(next);
        if (item) changeLine(item.dataset.cartItemKey, next);
        return;
      }
      // Remove
      var rmBtn = ev.target.closest('[data-alm-cart-remove]');
      if (rmBtn) {
        ev.preventDefault();
        var rmItem = rmBtn.closest('[data-cart-item-key]');
        if (rmItem) changeLine(rmItem.dataset.cartItemKey, 0);
        return;
      }
    });

    document.addEventListener('change', function (ev) {
      var input = ev.target.closest('[data-alm-qty-input]');
      if (!input) return;
      var item = input.closest('[data-cart-item-key]');
      if (!item) return;
      var qty = Math.max(0, Number(input.value || 0));
      changeLine(item.dataset.cartItemKey, qty);
    });

    // Add-to-cart form interception (any form posting to /cart/add)
    document.addEventListener('submit', function (ev) {
      var form = ev.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = (form.getAttribute('action') || '').toLowerCase();
      if (action.indexOf('/cart/add') === -1) return;
      // Skip native checkout-bypass forms
      if (form.hasAttribute('data-skip-drawer')) return;
      ev.preventDefault();
      var fd = new FormData(form);
      addItem(fd).then(function () { open(); }).catch(function (err) {
        // Fallback: native submit so user still sees an error
        console.warn('alm-cart-drawer add failed:', err);
        form.removeEventListener('submit', arguments.callee);
        form.submit();
      });
    });

    // Esc to close
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && drawer && drawer.classList.contains('is-open')) close();
    });
  }

  // ---------- order note ----------
  function bindOrderNote() {
    if (!noteInput) return;
    var save = function () {
      var val = noteInput.value || '';
      if (noteStatus) noteStatus.textContent = 'Saving…';
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ note: val })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          if (noteStatus) {
            noteStatus.textContent = 'Saved';
            setTimeout(function () { if (noteStatus) noteStatus.textContent = ''; }, 1400);
          }
        })
        .catch(function () {
          if (noteStatus) noteStatus.textContent = 'Could not save — try again.';
        });
    };
    noteInput.addEventListener('input', function () {
      clearTimeout(noteSaveTimer);
      noteSaveTimer = setTimeout(save, 600);
    });
    noteInput.addEventListener('blur', function () {
      clearTimeout(noteSaveTimer);
      save();
    });
  }

  function init() {
    drawer = $('[data-alm-cart-drawer]');
    if (!drawer) return;
    panel = $('.alm-cart-drawer__panel', drawer);
    body = $('[data-alm-cart-body]', drawer);
    footer = $('[data-alm-cart-footer]', drawer);
    count = $('[data-alm-cart-count]', drawer);
    subtotal = $('[data-alm-cart-subtotal]', drawer);
    progress = $('[data-alm-cart-progress]', drawer);
    progressText = $('[data-alm-cart-progress-text]', drawer);
    progressFill = $('[data-alm-cart-progress-fill]', drawer);
    noteWrap = $('[data-alm-cart-note-wrap]', drawer);
    noteInput = $('[data-alm-cart-note]', drawer);
    noteStatus = $('[data-alm-cart-note-status]', drawer);
    xsellWrap = $('[data-alm-cart-xsell-wrap]', drawer);
    headerCountBadge = $('.alm-header__cart-count');
    threshold = Number(drawer.getAttribute('data-threshold') || 0);
    // Capture initial item count so the first add triggers a bump
    var initialCountStr = (count && count.textContent) || '0';
    lastItemCount = Number(initialCountStr) || 0;
    bindDelegation();
    bindOrderNote();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
